/**
 * Feature Flags Service
 * Handles feature flag management and evaluation
 */

import { api } from './api'

export type FlagType = 'BOOLEAN' | 'PERCENTAGE' | 'VARIANT'

export interface FeatureFlag {
  id: string
  key: string
  name: string
  description?: string
  flag_type: FlagType
  default_value: boolean
  rollout_percentage: number
  targeting_rules: Record<string, unknown>
  category?: string
  is_system: boolean
  scheduled_from?: string
  scheduled_to?: string
  created_at: string
  updated_at?: string
  created_by?: string
  updated_by?: string
  is_active: boolean
  override_count: number
}

export interface FeatureFlagCreate {
  key: string
  name: string
  description?: string
  flag_type: FlagType
  default_value: boolean
  rollout_percentage?: number
  targeting_rules?: Record<string, unknown>
  category?: string
  scheduled_from?: string
  scheduled_to?: string
}

export interface FeatureFlagUpdate {
  name?: string
  description?: string
  flag_type?: FlagType
  default_value?: boolean
  rollout_percentage?: number
  targeting_rules?: Record<string, unknown>
  scheduled_from?: string
  scheduled_to?: string
  category?: string
}

export interface FeatureFlagOverride {
  id: string
  feature_flag_id: string
  feature_flag?: FeatureFlag
  business_unit_id: string
  value: boolean
  reason?: string
  valid_from?: string
  valid_to?: string
  created_at: string
  updated_at?: string
}

export interface FeatureFlagListResponse {
  items: FeatureFlag[]
  total: number
  page: number
  per_page: number
}

export interface FeatureFlagEvaluateRequest {
  user_id?: string
  business_unit_id?: string
  context?: Record<string, unknown>
}

export interface FeatureFlagEvaluateResponse {
  flag_key: string
  enabled: boolean
  reason: string
}

export interface FeatureFlagBulkEvaluateResponse {
  evaluations: FeatureFlagEvaluateResponse[]
}

class FeatureFlagService {
  private baseUrl = '/api/v1/feature-flags'

  // Feature Flags CRUD
  async getFeatureFlags(params?: { category?: string; skip?: number; limit?: number }): Promise<FeatureFlagListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.category) queryParams.append('category', params.category)
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    return api.get<FeatureFlagListResponse>(`${this.baseUrl}/flags?${queryParams.toString()}`)
  }

  async getFeatureFlag(id: string): Promise<FeatureFlag> {
    return api.get<FeatureFlag>(`${this.baseUrl}/flags/${id}`)
  }

  async createFeatureFlag(data: FeatureFlagCreate): Promise<FeatureFlag> {
    return api.post<FeatureFlag>(`${this.baseUrl}/flags`, data)
  }

  async updateFeatureFlag(id: string, data: FeatureFlagUpdate): Promise<FeatureFlag> {
    return api.put<FeatureFlag>(`${this.baseUrl}/flags/${id}`, data)
  }

  async deleteFeatureFlag(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/flags/${id}`)
  }

  // Feature Flag Overrides
  async getOverrides(flagId: string): Promise<{ items: FeatureFlagOverride[]; total: number }> {
    return api.get<{ items: FeatureFlagOverride[]; total: number }>(
      `${this.baseUrl}/flags/${flagId}/overrides`
    )
  }

  async createOverride(flagId: string, data: { business_unit_id: string; value: boolean; reason?: string }): Promise<FeatureFlagOverride> {
    return api.post<FeatureFlagOverride>(
      `${this.baseUrl}/flags/${flagId}/overrides`,
      data
    )
  }

  async updateOverride(flagId: string, overrideId: string, data: { value?: boolean; reason?: string }): Promise<FeatureFlagOverride> {
    return api.put<FeatureFlagOverride>(
      `${this.baseUrl}/flags/${flagId}/overrides/${overrideId}`,
      data
    )
  }

  async deleteOverride(flagId: string, overrideId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/flags/${flagId}/overrides/${overrideId}`)
  }

  // Evaluation
  async evaluateFlag(flagKey: string, data?: FeatureFlagEvaluateRequest): Promise<FeatureFlagEvaluateResponse> {
    return api.post<FeatureFlagEvaluateResponse>(
      `${this.baseUrl}/evaluate/${flagKey}`,
      data
    )
  }

  async bulkEvaluateFlags(data: FeatureFlagEvaluateRequest): Promise<FeatureFlagBulkEvaluateResponse> {
    return api.post<FeatureFlagBulkEvaluateResponse>(
      `${this.baseUrl}/evaluate-all`,
      data
    )
  }
}

export const featureFlagService = new FeatureFlagService()
